/**
 * Media Session: what the OS shows for this page's audio (iOS Dynamic Island /
 * lock screen, Android notification, desktop media keys). Without metadata iOS
 * shows a generic waveform. Song title + app icon, and play/pause from the
 * system controls drive the transport.
 */
import type { EngineController } from "./controller";

export const APP_NAME = "UmculoLandi"; // working title (AGENT.md: not a permanent brand)

type MediaSessionLike = Pick<MediaSession, "setActionHandler"> & { metadata: MediaMetadata | null; playbackState: MediaSessionPlaybackState };

export interface MediaSessionDeps {
  mediaSession?: MediaSessionLike;
  createMetadata?: (init: MediaMetadataInit) => MediaMetadata;
  /** Base URL the icons are served under (Vite BASE_URL). */
  base?: string;
}

export function bindMediaSession(c: EngineController, deps: MediaSessionDeps = {}): () => void {
  const ms = deps.mediaSession ?? (globalThis.navigator as Navigator | undefined)?.mediaSession;
  const Meta = deps.createMetadata ?? (typeof MediaMetadata === "function" ? (i: MediaMetadataInit) => new MediaMetadata(i) : undefined);
  if (!ms || !Meta) return () => {};
  const base = deps.base ?? "/";
  const artwork = [
    { src: `${base}icons/icon-192.png`, sizes: "192x192", type: "image/png" },
    { src: `${base}icons/icon-512.png`, sizes: "512x512", type: "image/png" },
  ];
  let lastTitle: string | null = null;
  const update = () => {
    const song = c.project.get().song;
    const title = song.title.trim() || "Untitled song";
    if (title !== lastTitle) {
      lastTitle = title;
      ms.metadata = Meta({ title, artist: song.artist.trim() || APP_NAME, album: APP_NAME, artwork });
    }
    ms.playbackState = c.live.get().playing ? "playing" : "paused";
  };
  const handlers: [MediaSessionAction, () => void][] = [
    ["play", () => void c.play()],
    ["pause", () => c.stop()],
    ["stop", () => c.stop()],
  ];
  for (const [action, fn] of handlers) {
    try { ms.setActionHandler(action, fn); } catch { /* action unsupported here */ }
  }
  update();
  const unsubs = [c.project.subscribe(update), c.live.subscribe(update)];
  return () => {
    unsubs.forEach((u) => u());
    for (const [action] of handlers) { try { ms.setActionHandler(action, null); } catch { /* ignore */ } }
  };
}
